import React, { useEffect, useState } from "react";
import { LicenseJsonModel } from "../../models/BlockTransportSystemJsonModel";
import GlobalEnvironments from "../../constants/GlobalEnvironments";
import { convertDatetime, getDateWithoutSeconds } from "../../utils/DateHelper";
import { Button, Col, Form, Modal, Row, Stack, Image, Spinner } from "react-bootstrap";
import { UserPermissionModel } from "../../controllers/StudioAdminController";
import BlockKitStore from "../../stores/BlockKitStore";
import StudioUserStore from "../../stores/StudioUserStore";
import { inject, observer } from "mobx-react";
import { useTranslation } from "react-i18next";

interface IProps {
  blockKitStore?: BlockKitStore;
  studioUserStore?: StudioUserStore;
  license?: LicenseJsonModel;
  timeZone?: string;
  isShow: boolean;
  isCreateMode: boolean;
  onClickSave: (license: LicenseJsonModel, file?: File) => Promise<void>;
  onClickClose: () => void;
}

const LicenseInfoCard: React.FC<IProps> = inject("blockKitStore", "studioUserStore")(observer(({
  blockKitStore,
  studioUserStore,
  license,
  timeZone,
  isShow,
  isCreateMode,
  onClickSave,
  onClickClose
}: IProps) => {
  const [thumbnailFile, setThumbnailFile] = useState<File>();
  const [thumbnailLocalUrl, setThumbnailLocalUrl] = useState<string>();
  const [isChangedThumbnail, setIsChangedThumbnail] = useState<boolean>();
  const [inputs, setInputs] = useState({
    name: license?.name ?? "",
    releaseDate: license?.releaseDate ?? getDateWithoutSeconds(),
    expireDate: license?.expireDate ?? new Date(2100, 11, 31),
  });
  const [permissions, setPermissions] = useState<UserPermissionModel[]>([]);
  const [isWorking, setIsWorking] = useState<boolean>(false);
  const { t } = useTranslation();

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;
    setInputs({
      ...inputs,
      [name]: (name === "releaseDate" || name === "expireDate")
        ? new Date(value)
        : value,
    });
  };

  const handleThumbnailFileOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      return;
    }

    let file = e.target.files[0];
    let reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      if (base64) {
        setThumbnailFile(file);
      }
    };
    reader.readAsDataURL(file);
  };

  const getThumbnail = () => {
    return license?.thumbnailUrl ? `${GlobalEnvironments.getBlockTransportSystemHostUrlForWeb()}${license?.thumbnailUrl}` : "";
  }

  useEffect(() => {
    if (thumbnailFile) {
      if (thumbnailLocalUrl) {
        URL.revokeObjectURL(thumbnailLocalUrl);
      }

      setThumbnailLocalUrl(URL.createObjectURL(thumbnailFile));
    }
  }, [thumbnailFile])

  useEffect(() => {
  }, [thumbnailLocalUrl])

  useEffect(() => {
    if (license?.id) {
      blockKitStore?.studioController.getEntityPermissionsAsync("licenses", license.id).then(setPermissions);
    }

    return () => {
      if (thumbnailLocalUrl) {
        URL.revokeObjectURL(thumbnailLocalUrl);
        setThumbnailLocalUrl("")
      }
    }
  }, []);

  useEffect(() => {
    if (thumbnailLocalUrl) {
      URL.revokeObjectURL(thumbnailLocalUrl);
      setThumbnailLocalUrl("")
    }
    setThumbnailFile(undefined);
    setInputs({
      name: license?.name ?? "",
      releaseDate: license?.releaseDate ?? getDateWithoutSeconds(),
      expireDate: license?.expireDate ?? new Date(2100, 11, 31),
    });
  }, [license]);

  return (
    <Modal show={isShow} className="border rounded mt-2 mb-2 p-2" onHide={() => onClickClose()}>
      <Modal.Header closeButton>{t("license")}</Modal.Header>
      <Modal.Body>
        <Stack gap={1}>
          <Form.Group as={Row} className="mb-1">
            <Row className="align-items-center">
              <Col sm={3}>
                <Form.Label column>{t("thumbnail")}</Form.Label>
              </Col>
              <Col sm={5}>
                <a
                  href={thumbnailLocalUrl ?? getThumbnail()}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    const url = thumbnailLocalUrl ?? getThumbnail();
                    if (!url) {
                      e.preventDefault();
                    }
                  }}
                >
                  <Image
                    src={thumbnailLocalUrl ?? getThumbnail()}
                    alt={t("thumbnail")}
                    thumbnail
                    style={{ width: "300px", height: "120px", objectFit: "cover" }}
                  />
                </a>
              </Col>
              <Col sm={4}>
                <Form.Control
                  id="thumbnail"
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    e.preventDefault();
                    handleThumbnailFileOnChange(e as React.ChangeEvent<HTMLInputElement>);
                    setIsChangedThumbnail(true);
                  }}
                />
                <Form.Label htmlFor="thumbnail" className="btn btn-outline-primary">{t("select-file")}</Form.Label>
              </Col>
            </Row>
          </Form.Group>
          <Form.Group as={Row} hidden={isCreateMode}>
            <Form.Label column sm={3}>{t("id")}</Form.Label>
            <Col>
              <Form.Control value={license?.id} readOnly />
            </Col>
          </Form.Group>
          <Form.Group as={Row}>
            <Form.Label column sm={3}>{t("name")}</Form.Label>
            <Col>
              <Form.Control
                type={"text"}
                name={"name"}
                value={inputs?.name}
                placeholder={t("name")}
                onChange={onChange} />
            </Col>
          </Form.Group>
          <Form.Group as={Row}>
            <Form.Label column sm={3}>{t("release-date")}</Form.Label>
            <Col>
              <Form.Control
                type={"datetime-local"}
                name={"releaseDate"}
                value={inputs.releaseDate ? convertDatetime(new Date(inputs.releaseDate), timeZone) : ""}
                onChange={onChange} />
            </Col>
            ({timeZone})
          </Form.Group>
          <Form.Group as={Row}>
            <Form.Label column sm={3}>{t("expire-date")}</Form.Label>
            <Col>
              <Form.Control
                type={"datetime-local"}
                name={"expireDate"}
                value={inputs.expireDate ? convertDatetime(new Date(inputs.expireDate), timeZone) : ""}
                onChange={onChange} />
            </Col>
            ({timeZone})
          </Form.Group>
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <Button
          disabled={(!isCreateMode && !studioUserStore?.hasWritePermission(permissions)) || isWorking}
          onClick={async () => {
            setIsWorking(true);
            try {
              await onClickSave({
                ...license,
                ...inputs
              },
                isChangedThumbnail ? thumbnailFile : undefined);

            } finally {
              setIsWorking(false);
            }
          }}
        >
          {isWorking &&
            <Spinner
              className="me-1"
              animation="border"
              size="sm"
              role="status"
            />}
          {t("button-save")}
        </Button>
        <Button disabled={isWorking} variant="secondary" onClick={() => onClickClose()}>{t("button-close")}</Button>
      </Modal.Footer>
    </Modal>
  )
}));

export default LicenseInfoCard;